import { PLAQUE_SMALL, LABEL } from './hud-theme';
import { questRewardMarkup } from './quest-progress';
import { t } from '../i18n/i18n';

/**
 * "Quest complete" during a run (2026-09-01).
 *
 * User: "If I complete a quest during gameplay, there should be an in-game
 * display showing that I completed the quest."
 *
 * Before this, finishing a daily mid-run was completely invisible. The core
 * `QuestService.trackAction()` flips the row to COMPLETED in Postgres and says
 * nothing, so the only way to find out was to survive, extract, and open the
 * hub — by which point it reads as bookkeeping rather than as something you
 * just did.
 *
 * ## Why a corner toast and not a `level-up-banner.ts`-style announcement
 *
 * The level-up banner takes the middle of the screen because a level is the
 * rarest good thing in a run. A quest tick is not that: three dailies plus a
 * weekly means several a session, and one of the four families
 * (`LOOT_EXTRACT_*`, `RUN_EXTRACT`) fires during extraction, when the player
 * is being told several other things at once. Centre-screen would be shouting.
 *
 * Mid-right is the one edge of the HUD nothing else claims — the party list is
 * top-right, the loot panel bottom-right, the action bar bottom-centre, the
 * target frame top-centre. `pointer-events-none` throughout: this appears
 * mid-fight and must never eat a click meant for the world.
 *
 * Deliberately says nothing about claiming. `design/gdd/quests.md` keeps
 * rewards decoupled from the room ("claiming happens later via REST, entirely
 * decoupled from the Colyseus room"), so this reports the fact and the hub
 * hands over the reward.
 */

/** Long enough to read three words during a fight, short enough to be gone before the next opening. */
const HOLD_MS = 4_000;
const FADE_MS = 320;
/** More than this on screen at once is a wall, not information. Oldest goes first. */
const MAX_VISIBLE = 3;

export interface QuestCompleteNotice {
  name: string;
  description?: string;
  rewardXp?: number;
  rewardSoft?: number;
  rewardPremium?: number;
}

/**
 * The reward line, or null when a quest carries none.
 *
 * Pure and exported so the wording is testable without a DOM: "+25 XP" is the
 * whole payoff being reported, and a quest whose reward silently renders as
 * "+0 XP" would read as a bug to the player.
 */
export function rewardLine(notice: QuestCompleteNotice): string | null {
  const parts: string[] = [];
  if (notice.rewardXp) parts.push(t('+{n} XP', { n: notice.rewardXp }));
  if (notice.rewardSoft) parts.push(t('+{n} Wax', { n: notice.rewardSoft }));
  if (notice.rewardPremium) parts.push(t('+{n} Ember', { n: notice.rewardPremium }));
  return parts.length > 0 ? parts.join('  ·  ') : null;
}

/**
 * The same line with the currency glyphs in it (2026-09-02).
 *
 * Kept beside `rewardLine` rather than replacing it: the plain-text version
 * is still what a caller wants when it is setting `textContent`, and the
 * wording is what the tests pin. This one is for the two places that render
 * a reward prominently, where "+40 Wax" and "+2 Ember" should be as
 * recognisable here as they are on a price tag.
 */
export function rewardLineMarkup(notice: QuestCompleteNotice): string | null {
  // The parts come from `questRewardMarkup` (2026-09-05) so the banner and the
  // hub row cannot disagree about which currencies a quest can pay; this keeps
  // only the wrapper, which is what makes it a *line*.
  const parts = questRewardMarkup(notice);
  if (!parts) return null;
  return `<span class="inline-flex items-center gap-2 flex-wrap">${parts}</span>`;
}

export class QuestCompleteBanner {
  private readonly root: HTMLDivElement;
  private readonly timers = new Set<ReturnType<typeof setTimeout>>();
  private readonly reducedMotion: boolean;

  constructor(container: HTMLElement) {
    this.reducedMotion = typeof window !== 'undefined'
      && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

    this.root = document.createElement('div');
    this.root.className = [
      'fixed', 'z-[1180]', 'pointer-events-none', 'select-none',
      'right-[max(0.75rem,env(safe-area-inset-right))]', 'top-1/3',
      'w-[15rem]', 'max-w-[62vw]', 'flex', 'flex-col', 'gap-2',
    ].join(' ');
    container.appendChild(this.root);
  }

  show(notice: QuestCompleteNotice): void {
    const card = document.createElement('div');
    card.className = `${PLAQUE_SMALL} px-2.5 py-2 flex items-start gap-2`;
    card.style.opacity = '0';
    card.style.transform = 'translateX(12px)';
    card.style.transition = `opacity ${FADE_MS}ms ease, transform ${FADE_MS}ms ease`;

    // A brass tick, matching the role badge on the player frame. A glyph
    // rather than the word "done" so the card reads before it is read.
    const tick = document.createElement('div');
    tick.className = 'shrink-0 w-5 h-5 rounded-full border border-[#514132] bg-warn '
      + 'text-[#241309] text-[12px] leading-none font-bold flex items-center justify-center';
    tick.textContent = '✓';

    const text = document.createElement('div');
    text.className = 'min-w-0 flex flex-col gap-0.5';

    const heading = document.createElement('div');
    heading.className = `font-heading font-bold text-[12px] ${LABEL}`;
    heading.style.color = '#e0a640';
    heading.textContent = t('Quest complete');

    const name = document.createElement('div');
    name.className = `text-[12px] font-semibold leading-tight ${LABEL}`;
    name.textContent = t(notice.name);

    text.append(heading, name);

    const reward = rewardLineMarkup(notice);
    if (reward) {
      const rewardEl = document.createElement('div');
      rewardEl.className = `text-[11px] font-semibold ${LABEL}`;
      rewardEl.style.color = '#ffd98a';
      rewardEl.innerHTML = reward;
      text.appendChild(rewardEl);
    }

    card.append(tick, text);
    this.root.appendChild(card);

    // Oldest first, so a burst at extraction does not fill the screen.
    while (this.root.children.length > MAX_VISIBLE) this.root.firstElementChild?.remove();

    // Next frame, so the transition has a start value to move from. A
    // `requestAnimationFrame` that never fires (a hidden tab) must not leave
    // the card invisible forever, so the value is also set directly when
    // motion is reduced.
    if (this.reducedMotion || typeof requestAnimationFrame !== 'function') {
      card.style.opacity = '1';
      card.style.transform = 'none';
    } else {
      requestAnimationFrame(() => { card.style.opacity = '1'; card.style.transform = 'translateX(0)'; });
    }

    const timer = setTimeout(() => {
      card.style.opacity = '0';
      card.style.transform = 'translateX(12px)';
      const removal = setTimeout(() => { card.remove(); this.timers.delete(removal); }, FADE_MS);
      this.timers.add(removal);
      this.timers.delete(timer);
    }, HOLD_MS);
    this.timers.add(timer);
  }

  dispose(): void {
    for (const timer of this.timers) clearTimeout(timer);
    this.timers.clear();
    this.root.remove();
  }
}
